import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle, Upload, X, Lock, Check, AlertTriangle, Eye, EyeOff, Gift, Copy, Loader2, User } from 'lucide-react';
import type { UserProfile } from '../types';
import { storageService } from '../lib/services/storageService';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export function ProfileForm() {
  const { user, updateProfile, linkGoogleAccount, getUserIdentities, updatePassword, generateReferralCode } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    username: '',
    full_name: '',
    avatar_url: '',
    bio: '',
    favorite_categories: [],
  });
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [linkedIdentities, setLinkedIdentities] = useState<any[]>([]);
  const [loadingIdentities, setLoadingIdentities] = useState(true);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [linkMessage, setLinkMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [isModeratingAvatar, setIsModeratingAvatar] = useState(false);
  const [isModeratingBio, setIsModeratingBio] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para alteração de senha
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Estados para validação de senha
  const [passwordRequirements, setPasswordRequirements] = useState<string[]>([]);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean | null>(null);
  
  // Estados para mostrar/ocultar senhas
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados para o código de referência
  const [localReferralCode, setLocalReferralCode] = useState<string | null | undefined>(user?.referral_code);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  // Estados para a funcionalidade "Quem te indicou?"
  const [referralInputCode, setReferralInputCode] = useState('');
  const [verifyingReferralCode, setVerifyingReferralCode] = useState(false);
  const [referralCodeError, setReferralCodeError] = useState<string | null>(null);
  const [verifiedReferrerProfile, setVerifiedReferrerProfile] = useState<Partial<UserProfile> | null>(null);
  const [confirmingReferral, setConfirmingReferral] = useState(false);
  const [confirmedReferrerProfile, setConfirmedReferrerProfile] = useState<Partial<UserProfile> | null>(null);
  const [loadingConfirmedReferrer, setLoadingConfirmedReferrer] = useState(false);

  // Manter localReferralCode sincronizado com user.referral_code
  useEffect(() => {
    setLocalReferralCode(user?.referral_code);
  }, [user?.referral_code]);

  // Buscar dados do usuário que indicou, se já existir user.referred_by_id
  useEffect(() => {
    const fetchConfirmedReferrer = async () => {
      if (user?.referred_by_id) {
        setLoadingConfirmedReferrer(true);
        try {
          const { data: referrerProfile, error } = await supabase
            .from('profiles')
            .select('id, username, full_name, avatar_url')
            .eq('id', user.referred_by_id)
            .single();

          if (error) {
            console.error('[ProfileForm] Erro ao buscar perfil do referente confirmado:', error);
            setReferralCodeError('Não foi possível carregar os dados de quem te indicou.');
          } else if (referrerProfile) {
            setConfirmedReferrerProfile({
              ...referrerProfile,
              username: referrerProfile.username || undefined,
              full_name: referrerProfile.full_name || undefined,
              avatar_url: referrerProfile.avatar_url || undefined,
            });
          }
        } catch (e) {
          console.error('[ProfileForm] Exceção ao buscar perfil do referente confirmado:', e);
          setReferralCodeError('Erro ao carregar dados da indicação.');
        } finally {
          setLoadingConfirmedReferrer(false);
        }
      }
    };
    fetchConfirmedReferrer();
  }, [user?.referred_by_id, supabase]);

  // Carregar identidades vinculadas quando o componente montar
  useEffect(() => {
    async function loadUserIdentities() {
      if (!user) return;
      
      setLoadingIdentities(true);
      setIdentityError(null);
      
      try {
        const { success, identities, error } = await getUserIdentities();
        
        if (success && identities) {
          setLinkedIdentities(identities);
        } else if (error) {
          setIdentityError(error);
        }
      } catch (error) {
        setIdentityError('Erro ao buscar identidades vinculadas');
        console.error('[ProfileForm] Erro ao buscar identidades:', error);
      } finally {
        setLoadingIdentities(false);
      }
    }
    
    loadUserIdentities();
  }, [user, getUserIdentities]);

  // Verificar se já existe identidade do Google
  const hasGoogleIdentity = linkedIdentities.some(
    identity => identity.provider === 'google'
  );

  useEffect(() => {
    setLoading(true);
    if (user) {
      setFormData({
        username: user.username || '',
        full_name: user.full_name || '',
        avatar_url: user.avatar_url || '',
        bio: user.bio || '',
        favorite_categories: user.favorite_categories || [],
      });
      
      // Definir preview do avatar se já existir
      if (user.avatar_url) {
        setAvatarPreview(user.avatar_url);
      }
      
      setLoading(false);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    setIsModeratingBio(false);

    try {
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      const payload: Partial<UserProfile> = { ...formData };

      if (user.username && payload.username && user.username !== payload.username) {
        setError('O nome de usuário não pode ser alterado após definido.');
        setSaving(false);
        return;
      } else if (!user.username && payload.username && payload.username.length < 3) {
        throw new Error('Nome de usuário deve ter pelo menos 3 caracteres');
      }

      if (user.username) {
        delete payload.username; 
      }

      if (payload.avatar_url && !payload.avatar_url.startsWith('http')) {
        throw new Error('URL do avatar deve ser um endereço válido começando com http:// ou https://');
      }

      // <<< INÍCIO DA MODERAÇÃO DE TEXTO DA BIO >>>
      if (payload.bio && payload.bio.trim() !== (user.bio || '').trim()) {
        setIsModeratingBio(true);
        const moderationToastId = toast.loading('Analisando texto da bio...');
        const { data: moderationResult, error: moderationInvokeError } = await supabase.functions.invoke('moderate-text', {
          body: { textContent: payload.bio.trim() } 
        });
        toast.dismiss(moderationToastId);
        
        if (moderationInvokeError) {
          console.error('[ProfileForm] Erro ao invocar função de moderação de texto para bio:', moderationInvokeError);
          toast.error('Falha ao verificar o texto da bio. Tente novamente.');
          setIsModeratingBio(false);
          setSaving(false);
          return;
        }

        if (moderationResult && !moderationResult.success) {
          const moderationMessage = moderationResult.error || 'Conteúdo da bio considerado impróprio.';
          console.warn(`[ProfileForm] Moderação de texto da bio falhou: ${moderationMessage}`);
          toast.error(moderationMessage);
          setIsModeratingBio(false);
          setSaving(false);
          return; 
        }
        
        if (moderationResult && moderationResult.success) {
          toast.success(moderationResult.message || 'Texto da bio verificado!');
        }
        setIsModeratingBio(false); 
      }
      // <<< FIM DA MODERAÇÃO DE TEXTO DA BIO >>>

      const result = await updateProfile({
        id: user.id,
        email: user.email,
        ...payload
      });

      if (!result.success) {
        throw new Error(result.error || 'Erro ao atualizar perfil');
      }

      setSuccess(true);
      toast.success('Perfil atualizado com sucesso!');
      // Atualizar o estado do usuário no AuthContext após a atualização bem-sucedida
      // Isso é importante para que a UI reflita as mudanças (ex: username novo, bio nova)
      // e para que a lógica de "username já existe" funcione corretamente sem refresh.
      const { data: updatedUser, error: fetchUserError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (fetchUserError) {
        console.error("Erro ao buscar usuário atualizado:", fetchUserError);
      } else if (updatedUser) {
        // Chamar uma função do AuthContext para atualizar o usuário, se existir
        // Exemplo: updateUserInContext(updatedUser); 
        // Por agora, vou apenas logar e assumir que o AuthContext se atualiza em algum momento
         console.log("Usuário atualizado localmente (simulação):", updatedUser);
         // Se você tiver uma função no AuthContext para atualizar o usuário:
         // refreshAuthUser(); // ou algo similar
      }

    } catch (err: any) {
      console.error('Erro ao salvar perfil:', err);
      setError(err.message || 'Erro ao atualizar perfil');
      toast.error(err.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
      setIsModeratingBio(false); 
    }
  };

  // Função para vincular conta do Google
  const handleLinkGoogleAccount = async () => {
    setIsLinkingGoogle(true);
    setLinkMessage({ text: 'Iniciando processo de vinculação...', type: 'info' });
    
    try {
      const { success, error } = await linkGoogleAccount();
      
      if (!success && error) {
        setLinkMessage({ text: error, type: 'error' });
      } else {
        // Se foi bem-sucedido, o usuário será redirecionado para o fluxo OAuth do Google
        setLinkMessage({ text: 'Você será redirecionado para o Google para vincular sua conta', type: 'info' });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      setLinkMessage({ text: `Erro ao vincular conta Google: ${errorMsg}`, type: 'error' });
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  // Função auxiliar para campos de texto
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Função para lidar com arrays (tags/categorias)
  const handleCategoriesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const categories = value.split(',').map(cat => cat.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, favorite_categories: categories }));
  };

  // Função para abrir o seletor de arquivo
  const handleAvatarButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Função para processar o upload do avatar
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const avatarFile = files[0];
    setUploadingAvatar(true);
    setError(null);
    let publicUrl: string | null = null;
    let filePathInBucket: string | null = null;
    
    try {
      // Criar URL temporária para preview imediato
      const previewUrl = URL.createObjectURL(avatarFile);
      setAvatarPreview(previewUrl);
      
      // Construir o caminho do arquivo de forma consistente
      const fileName = `avatar_${user?.id}_${Date.now()}.${avatarFile.name.split('.').pop()}`;
      filePathInBucket = `user_${user?.id}/${fileName}`; 
      
      // Fazer upload para o Supabase Storage
      const { url, error: uploadError } = await storageService.uploadImage(
        avatarFile, 
        'avatars', 
        filePathInBucket
      );
      
      if (uploadError || !url) {
        // Reverter preview se upload falhar
        setAvatarPreview(formData.avatar_url || null);
        throw new Error(uploadError || 'Falha ao fazer upload da imagem');
      }
      publicUrl = url;

      // >>> INÍCIO DA MODERAÇÃO DE IMAGEM <<<
      if (!filePathInBucket) {
        toast.error('Erro interno: Caminho do arquivo não definido para moderação.');
        setAvatarPreview(formData.avatar_url || null);
        setUploadingAvatar(false);
        return;
      }

      setIsModeratingAvatar(true);
      const moderationToastId = toast.loading('Analisando imagem do avatar...');

      const { data: moderationResult, error: moderationInvokeError } = await supabase.functions.invoke('moderate-image', {
        body: { imageUrl: publicUrl, imagePath: filePathInBucket } 
      });

      toast.dismiss(moderationToastId);
      setIsModeratingAvatar(false);

      if (moderationInvokeError) {
        console.error('[ProfileForm] Erro ao invocar função de moderação:', moderationInvokeError);
        toast.error('Falha ao verificar a imagem. Tente outra imagem.');
        setAvatarPreview(formData.avatar_url || null);
        if (publicUrl) {
            try { await storageService.deleteImage(publicUrl, 'avatars'); } catch (e) { console.error("Erro ao limpar imagem após falha de invocação", e);}
        }
        setUploadingAvatar(false);
        return;
      }

      if (moderationResult && !moderationResult.success) {
        const moderationMessage = moderationResult.error || 'Conteúdo da imagem do avatar considerado impróprio.';
        console.warn(`[ProfileForm] Moderação de avatar falhou: ${moderationMessage}`);
        toast.error(moderationMessage);
        setAvatarPreview(formData.avatar_url || null);
        setUploadingAvatar(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      
      if (moderationResult && moderationResult.success) {
        toast.success(moderationResult.message || 'Imagem do avatar verificada!');
      }
      // >>> FIM DA MODERAÇÃO DE IMAGEM <<<
      
      // Atualizar o formData com a nova URL (somente se moderação OK)
      setFormData(prev => ({
        ...prev,
        avatar_url: publicUrl || undefined
      }));
      
    } catch (err: any) {
      console.error('Erro no upload ou moderação de avatar:', err);
      setError(err.message || 'Erro ao fazer upload ou moderar o avatar');
      // Limpar preview em caso de erro não tratado pela moderação (ex: erro de rede no upload inicial)
      setAvatarPreview(formData.avatar_url || null);
      // Se publicUrl existe e não estávamos no meio da moderação (que já trata a deleção), tentar deletar.
      if (publicUrl && !isModeratingAvatar) {
          try { await storageService.deleteImage(publicUrl, 'avatars'); } catch (e) { console.error("Erro ao limpar imagem após falha geral", e);}
      }
    } finally {
      setUploadingAvatar(false);
      setIsModeratingAvatar(false);
      // Limpar o input de arquivo se não houve sucesso ou se foi um erro antes da moderação.
      // Se a moderação falhou, já limpamos acima.
      if (error || (uploadingAvatar && !isModeratingAvatar)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
         }
      }
    }
  };

  // Função para remover o avatar
  const handleRemoveAvatar = async () => {
    // Se não tiver avatar, não faz nada
    if (!formData.avatar_url) return;
    
    setUploadingAvatar(true);
    setError(null);
    
    try {
      // Se tiver uma URL do Supabase Storage, tentar excluir o arquivo
      if (formData.avatar_url.includes(window.location.hostname) || 
          formData.avatar_url.includes('storage.googleapis.com')) {
        await storageService.deleteImage(formData.avatar_url, 'avatars');
      }
      
      // Atualizar o formData removendo a URL
      setFormData(prev => ({
        ...prev,
        avatar_url: ''
      }));
      
      // Limpar o preview
      setAvatarPreview(null);
      
    } catch (err: any) {
      console.error('Erro ao remover avatar:', err);
      setError(err.message || 'Erro ao remover o avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Funções de validação de senha
  const validatePassword = (password: string): boolean => {
    // Requisitos de senha forte:
    // - Pelo menos 8 caracteres
    // - Pelo menos uma letra maiúscula
    // - Pelo menos uma letra minúscula
    // - Pelo menos um número
    // - Pelo menos um caractere especial
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    
    return minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };
  
  // Função para verificar quais requisitos de senha estão faltando
  const getPasswordRequirements = (password: string): string[] => {
    const requirements: string[] = [];
    
    if (password.length < 8) {
      requirements.push("pelo menos 8 caracteres");
    }
    if (!/[A-Z]/.test(password)) {
      requirements.push("pelo menos uma letra maiúscula");
    }
    if (!/[a-z]/.test(password)) {
      requirements.push("pelo menos uma letra minúscula");
    }
    if (!/[0-9]/.test(password)) {
      requirements.push("pelo menos um número");
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      requirements.push("pelo menos um caractere especial");
    }
    
    return requirements;
  };
  
  // Função para verificar correspondência de senhas
  const checkPasswordsMatch = () => {
    if (passwordData.newPassword && passwordData.confirmPassword) {
      setPasswordsMatch(passwordData.newPassword === passwordData.confirmPassword);
    } else {
      setPasswordsMatch(null);
    }
  };

  // Função para lidar com alteração de senha
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Atualizar valor no estado
    setPasswordData(prev => {
      const newState = { ...prev, [name]: value };
      
      // Verificar correspondência de senhas imediatamente
      if (name === 'newPassword' && newState.confirmPassword) {
        setTimeout(() => setPasswordsMatch(newState.newPassword === newState.confirmPassword), 10);
      }
      
      if (name === 'confirmPassword' && newState.newPassword) {
        setTimeout(() => setPasswordsMatch(newState.newPassword === newState.confirmPassword), 10);
      }
      
      return newState;
    });
    
    // Limpar erros e sucessos anteriores
    setPasswordError(null);
    setPasswordSuccess(false);
    
    // Se for a senha nova, validar requisitos
    if (name === 'newPassword') {
      setPasswordRequirements(getPasswordRequirements(value));
    }
  };

  // Função para alternar visibilidade da senha
  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
    }
  };

  // Função para submeter alteração de senha
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);
    setChangingPassword(true);

    try {
      // Validações
      if (!passwordData.currentPassword) {
        throw new Error('Informe sua senha atual');
      }

      if (!passwordData.newPassword) {
        throw new Error('Informe a nova senha');
      }
      
      // Validar requisitos de senha forte
      if (!validatePassword(passwordData.newPassword)) {
        const missingRequirements = getPasswordRequirements(passwordData.newPassword);
        throw new Error(`A senha deve conter ${missingRequirements.join(', ')}`);
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('A confirmação de senha não corresponde à nova senha');
      }

      // Chamar API para atualizar a senha
      const result = await updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (!result.success) {
        throw new Error(result.error || 'Erro ao atualizar senha');
      }

      // Limpar campos após sucesso
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Limpar validações
      setPasswordRequirements([]);
      setPasswordsMatch(null);
      
      setPasswordSuccess(true);
    } catch (err: any) {
      console.error('Erro ao alterar senha:', err);
      setPasswordError(err.message || 'Erro ao atualizar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleGenerateReferralCode = async () => {
    if (!user) return;
    setIsGeneratingCode(true);
    setGenerateError(null);
    try {
      const result = await generateReferralCode();
      if (result.success && result.code) {
        setLocalReferralCode(result.code);
        toast.success('Seu código de indicação foi gerado!');
      } else {
        setGenerateError(result.error || 'Falha ao gerar o código.');
        toast.error(result.error || 'Falha ao gerar o código de indicação.');
      }
    } catch (error: any) {
      setGenerateError(error.message || 'Erro desconhecido ao gerar código.');
      toast.error(error.message || 'Erro desconhecido ao gerar código de indicação.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleCopyReferralLink = () => {
    if (!localReferralCode) return;
    const referralLink = `${window.location.origin}/signup?ref=${localReferralCode}`;
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopiedReferral(true);
        toast.success('Link de indicação copiado!');
        setTimeout(() => setCopiedReferral(false), 2000);
      })
      .catch(err => {
        console.error('Erro ao copiar link de indicação:', err);
        toast.error('Erro ao copiar o link de indicação.');
      });
  };

  // Funções para a funcionalidade "Quem te indicou?"
  const handleVerifyReferralCode = async () => {
    if (!referralInputCode.trim()) {
      setReferralCodeError('Por favor, insira um código de referência.');
      return;
    }
    setVerifyingReferralCode(true);
    setReferralCodeError(null);
    setVerifiedReferrerProfile(null);

    try {
      const { data: referrer, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url')
        .eq('referral_code', referralInputCode.trim().toUpperCase())
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Nenhum usuário encontrado com esse código
          setReferralCodeError('Código de referência inválido ou não encontrado.');
        } else {
          console.error('[ProfileForm] Erro ao verificar código de referência:', error);
          setReferralCodeError('Erro ao verificar o código. Tente novamente.');
        }
        return;
      }

      if (referrer && referrer.id === user?.id) {
        setReferralCodeError('Você não pode se auto-indicar.');
        return;
      }

      if (referrer) {
        setVerifiedReferrerProfile({
          id: referrer.id,
          username: referrer.username || undefined,
          full_name: referrer.full_name || undefined,
          avatar_url: referrer.avatar_url || undefined,
        });
      } else {
        // Isso não deve acontecer se PGRST116 for tratado, mas por segurança
        setReferralCodeError('Código de referência inválido ou não encontrado.');
      }

    } catch (e) {
      console.error('[ProfileForm] Exceção ao verificar código de referência:', e);
      setReferralCodeError('Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setVerifyingReferralCode(false);
    }
  };

  const handleConfirmReferral = async () => {
    if (!verifiedReferrerProfile || !verifiedReferrerProfile.id || !user) {
      setReferralCodeError('Nenhum usuário referente selecionado ou usuário atual não encontrado.');
      return;
    }

    setConfirmingReferral(true);
    setReferralCodeError(null);

    try {
      // 1. Atualizar o perfil do usuário atual com o ID de quem o indicou
      const updateResult = await updateProfile({
        id: user.id,
        referred_by_id: verifiedReferrerProfile.id,
      });

      if (!updateResult.success || updateResult.error) {
        throw new Error(updateResult.error || 'Falha ao atualizar seu perfil com a indicação.');
      }

      // 2. Registrar a indicação na tabela user_referrals
      const { error: referralInsertError } = await supabase
        .from('user_referrals')
        .insert({
          referrer_id: verifiedReferrerProfile.id, // Quem indicou
          referred_id: user.id,                  // Quem foi indicado (usuário atual)
          referral_code_used: referralInputCode.trim().toUpperCase(),
        });

      if (referralInsertError) {
        // Tentar reverter a atualização do perfil? Para este caso, talvez apenas logar e informar o usuário.
        // Idealmente, isso seria uma transação.
        console.error('[ProfileForm] Erro ao inserir em user_referrals:', referralInsertError);
        // É importante notificar o usuário que o perfil foi atualizado mas o registro da indicação falhou
        // para que ele possa contactar o suporte se necessário.
        toast.error('Seu perfil foi atualizado, mas houve um erro ao registrar os detalhes da indicação. Por favor, contate o suporte.');
        // Poderíamos tentar reverter a atualização do referred_by_id aqui se a política de negócios exigir.
        // Ex: await updateProfile({ id: user.id, referred_by_id: null });
        // Mas isso adiciona complexidade. Por ora, vamos apenas notificar.
        throw new Error('Falha ao registrar os detalhes da indicação após atualizar o perfil.');
      }
      
      // 3. Sucesso
      setConfirmedReferrerProfile(verifiedReferrerProfile);
      setVerifiedReferrerProfile(null);
      setReferralInputCode('');
      toast.success(`Você foi indicado por ${verifiedReferrerProfile.username || 'usuário'}!`);

      // Atualizar o estado do usuário no AuthContext para refletir o referred_by_id
      // (O AuthContext deve ter uma maneira de recarregar/atualizar o user)
      // Ex: refreshAuthUser(); // Esta função não existe aqui, é um exemplo

    } catch (err: any) {
      console.error('[ProfileForm] Erro ao confirmar indicação:', err);
      setReferralCodeError(err.message || 'Ocorreu um erro ao confirmar a indicação.');
      toast.error(err.message || 'Erro ao confirmar indicação.');
    } finally {
      setConfirmingReferral(false);
    }
  };

  if (loading) {
    return <div className={`text-center py-12 ${theme === 'dark' ? 'text-foreground' : 'text-gray-700'}`}>Carregando...</div>;
  }

  return (
    <div className={`max-w-2xl mx-auto p-6 ${theme === 'dark' ? 'text-foreground' : 'bg-white text-gray-900'}`}>
      <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Perfil do Usuário</h2>
      
      {error && (
        <div className={`border px-4 py-3 rounded mb-6 flex items-center gap-2 ${theme === 'dark' ? 'bg-red-900/20 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className={`border px-4 py-3 rounded mb-6 flex items-center gap-2 ${theme === 'dark' ? 'bg-green-900/20 border-green-500/30 text-green-400' : 'bg-green-50 border-green-200 text-green-600'}`}>
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span>Perfil atualizado com sucesso!</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Nome de Usuário
          </label>
          <input
            name="username"
            type="text"
            value={formData.username || ''}
            onChange={handleInputChange}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'} ${user?.username ? (theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed') : ''}`}
            placeholder="nomeusuario"
            disabled={!!user?.username || saving}
            aria-describedby={user?.username ? "username-readonly-note" : "username-note"}
          />
          <p id={user?.username ? "username-readonly-note" : "username-note"} className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Seu identificador único na plataforma. Mínimo de 3 caracteres.
            {user?.username && <span className={`ml-1 font-semibold ${theme === 'dark' ? 'text-yellow-400' : 'text-amber-600'}`}>(Não pode ser alterado após definido)</span>}
          </p>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Nome Completo
          </label>
          <input
            name="full_name"
            type="text"
            value={formData.full_name || ''}
            onChange={handleInputChange}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
            placeholder="Seu Nome Completo"
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Foto de Perfil
          </label>
          
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {avatarPreview ? (
                <div className="relative">
                  <img 
                    src={avatarPreview} 
                    alt="Avatar" 
                    className={`w-24 h-24 rounded-full object-cover border ${theme === 'dark' ? 'border-border' : 'border-gray-300'}`}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    disabled={uploadingAvatar}
                    className={`absolute -top-2 -right-2 p-1 shadow-sm transition ${theme === 'dark' ? 'bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90' : 'bg-red-500 text-white rounded-full hover:bg-red-600'}`}
                    title="Remover avatar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className={`w-24 h-24 rounded-full flex items-center justify-center border ${theme === 'dark' ? 'bg-muted border-border text-muted-foreground' : 'bg-gray-200 text-gray-500 border-gray-300'}`}>
                  <span className="text-2xl font-bold">
                    {formData.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex-grow space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
              
              <Button
                type="button"
                variant="outline"
                onClick={handleAvatarButtonClick}
                disabled={uploadingAvatar}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingAvatar ? 'Enviando...' : 'Fazer upload de foto'}
              </Button>
              
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 2MB.
              </p>
              
              <div className="mt-3">
                <input
                  name="avatar_url"
                  type="url"
                  value={formData.avatar_url || ''}
                  onChange={handleInputChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
                  placeholder="https://exemplo.com/seuperfil.jpg"
                />
                <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Ou informe a URL da sua imagem de perfil.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Bio
          </label>
          <textarea
            name="bio"
            value={formData.bio || ''}
            onChange={handleInputChange}
            rows={3}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
            placeholder="Conte um pouco sobre você e seus jogos favoritos..."
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Categorias Favoritas
          </label>
          <input
            name="categories"
            type="text"
            value={formData.favorite_categories?.join(', ') || ''}
            onChange={handleCategoriesChange}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
            placeholder="Estratégia, Cooperativo, Fantasy, Deck Building"
          />
          <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Separe as categorias por vírgulas.
          </p>
        </div>

        <div>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={saving || uploadingAvatar || isModeratingAvatar || isModeratingBio}
          >
            {isModeratingBio ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analisando Bio...</>
            ) : saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando...</>
            ) : (uploadingAvatar || isModeratingAvatar) ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processando Avatar...</>
            ) : (
              'Salvar Alterações'
            )}
          </Button>
        </div>
      </form>

      {/* Seção de Código de Referência */}
      <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-medium mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          <Gift className="h-5 w-5 mr-2 text-primary" />
          Programa de Indicação
        </h3>
        {localReferralCode ? (
          <div className="space-y-3">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Compartilhe seu código de indicação com amigos! Quando eles se cadastrarem usando seu código, vocês dois ganham recompensas.
            </p>
            <div className={`flex flex-col sm:flex-row items-center gap-3 p-3 border border-dashed rounded-lg ${theme === 'dark' ? 'border-primary/50 bg-primary/10' : 'border-indigo-300 bg-indigo-50'}`}>
              <span className={`text-lg font-mono tracking-wider select-all ${theme === 'dark' ? 'text-primary' : 'text-indigo-700'}`}>
                {localReferralCode}
              </span>
              <Button
                onClick={handleCopyReferralLink}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                {copiedReferral ? <CheckCircle className="mr-2 h-4 w-4 text-green-500" /> : <Copy className="mr-2 h-4 w-4" />}
                {copiedReferral ? 'Copiado!' : 'Copiar Link'}
              </Button>
            </div>
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Seu link de indicação é: <code className={`p-1 rounded text-xs ${theme === 'dark' ? 'bg-muted text-muted-foreground' : 'bg-gray-100'}`}>{`${window.location.origin}/signup?ref=${localReferralCode}`}</code>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Você ainda não gerou seu código de indicação. Gere seu código para começar a indicar amigos e ganhar recompensas!
            </p>
            <Button
              onClick={handleGenerateReferralCode}
              disabled={isGeneratingCode || !user}
              className="w-full sm:w-auto"
            >
              {isGeneratingCode ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Gerando Código...
                </span>
              ) : (
                <span className="flex items-center">
                  <Gift className="mr-2 h-4 w-4" /> Gerar Meu Código de Indicação
                </span>
              )}
            </Button>
            {generateError && (
              <p className={`text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{generateError}</p>
            )}
          </div>
        )}
      </div>

      {/* Seção Quem te Indicou? */}
      {user && (
        <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-medium mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            <User className="h-5 w-5 mr-2 text-primary" />
            Quem te indicou?
          </h3>

          {loadingConfirmedReferrer ? (
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Carregando informação de indicação...</p>
          ) : confirmedReferrerProfile?.username ? (
            <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-input border-border' : 'bg-gray-100 border-gray-200'}`}>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Você foi indicado por:
                <span className={`font-semibold ml-1 ${theme === 'dark' ? 'text-primary' : 'text-indigo-600'}`}>
                  {confirmedReferrerProfile.username}
                </span>
                {confirmedReferrerProfile.full_name && ` (${confirmedReferrerProfile.full_name})`}
              </p>
              {confirmedReferrerProfile.avatar_url && (
                <img 
                  src={confirmedReferrerProfile.avatar_url} 
                  alt={`Avatar de ${confirmedReferrerProfile.username}`}
                  className="w-12 h-12 rounded-full object-cover mt-2"
                />
              )}
            </div>
          ) : user.referred_by_id && !confirmedReferrerProfile ? (
             <p className={`${theme === 'dark' ? 'text-yellow-400' : 'text-amber-600'}`}>Carregando dados de quem te indicou...</p>
          ) : (
            <div className="space-y-4">
              {!verifiedReferrerProfile ? (
                <>
                  <div>
                    <Label htmlFor="referralInputCode" className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Código de Indicação (de quem te convidou)
                    </Label>
                    <Input
                      id="referralInputCode"
                      type="text"
                      value={referralInputCode}
                      onChange={(e) => setReferralInputCode(e.target.value.toUpperCase())}
                      placeholder="INSIRAOCODIGOAQUI"
                      className={`mt-1 ${theme === 'dark' ? 'bg-input border-border' : 'border-gray-300'}`}
                      disabled={verifyingReferralCode || confirmingReferral}
                    />
                  </div>
                  <Button 
                    onClick={handleVerifyReferralCode} 
                    disabled={verifyingReferralCode || confirmingReferral || !referralInputCode.trim()}
                  >
                    {verifyingReferralCode ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verificando...</>
                    ) : (
                      'Verificar Código'
                    )}
                  </Button>
                </>
              ) : (
                <div className={`p-4 rounded-md space-y-3 ${theme === 'dark' ? 'bg-input border-border' : 'bg-gray-100 border-gray-200'}`}>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Você confirma que foi indicado por:
                  </p>
                  <div className="flex items-center space-x-3">
                    {verifiedReferrerProfile.avatar_url && (
                      <img 
                        src={verifiedReferrerProfile.avatar_url} 
                        alt={`Avatar de ${verifiedReferrerProfile.username}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className={`font-semibold ${theme === 'dark' ? 'text-primary' : 'text-indigo-600'}`}>
                        {verifiedReferrerProfile.username}
                      </p>
                      {verifiedReferrerProfile.full_name && (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({verifiedReferrerProfile.full_name})
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleConfirmReferral} 
                      disabled={confirmingReferral || verifyingReferralCode}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {confirmingReferral ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Confirmando...</>
                      ) : (
                        <><CheckCircle className="mr-2 h-4 w-4"/> Sim, confirmar</>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setVerifiedReferrerProfile(null);
                        setReferralCodeError(null);
                        // Não limpar referralInputCode para facilitar correção
                      }}
                      disabled={confirmingReferral || verifyingReferralCode}
                    >
                      <X className="mr-2 h-4 w-4"/> Informar outro código
                    </Button>
                  </div>
                </div>
              )}

              {referralCodeError && (
                <div className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{referralCodeError}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Seção de alteração de senha */}
      <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-medium mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          <Lock className="h-5 w-5 mr-2 text-primary" />
          Alterar Senha
        </h3>
        
        {passwordError && (
          <div className={`border px-4 py-3 rounded mb-6 flex items-center gap-2 ${theme === 'dark' ? 'bg-red-900/20 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{passwordError}</span>
          </div>
        )}

        {passwordSuccess && (
          <div className={`border px-4 py-3 rounded mb-6 flex items-center gap-2 ${theme === 'dark' ? 'bg-green-900/20 border-green-500/30 text-green-400' : 'bg-green-50 border-green-200 text-green-600'}`}>
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span>Senha alterada com sucesso!</span>
          </div>
        )}
        
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Senha Atual
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm pr-10 ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
                placeholder="Informe sua senha atual"
                required
              />
              <button
                type="button"
                className={`absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => togglePasswordVisibility('current')}
                aria-label={showCurrentPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm pr-10 ${theme === 'dark' ? 'bg-input border-border text-foreground placeholder-muted-foreground' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`}
                placeholder="Informe sua nova senha"
                required
                minLength={8}
              />
              <button
                type="button"
                className={`absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => togglePasswordVisibility('new')}
                aria-label={showNewPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {passwordData.newPassword && passwordRequirements.length > 0 && (
              <div className="mt-2 text-xs">
                <p className={`font-medium flex items-center ${theme === 'dark' ? 'text-yellow-400' : 'text-amber-600'}`}>
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Sua senha deve conter:
                </p>
                <ul className={`mt-1 space-y-1 pl-5 list-disc ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {passwordRequirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {passwordData.newPassword && passwordRequirements.length === 0 && (
              <p className={`mt-1 text-xs flex items-center ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                <Check className="h-4 w-4 mr-1" />
                Senha forte
              </p>
            )}
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-ring sm:text-sm pr-10 ${theme === 'dark' ? 'bg-input text-foreground placeholder-muted-foreground' : 'text-gray-900 placeholder-gray-500'} 
                  ${passwordsMatch === true ? (theme === 'dark' ? 'border-green-500/70 focus:border-green-500' : 'border-green-300 focus:border-green-500') : 
                  passwordsMatch === false ? (theme === 'dark' ? 'border-red-500/70 focus:border-red-500' : 'border-red-300 focus:border-red-500') : 
                  (theme === 'dark' ? 'border-border focus:border-ring' : 'border-gray-300 focus:border-indigo-500')}`}
                placeholder="Confirme sua nova senha"
                required
              />
              
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  type="button"
                  className={`pr-3 flex items-center focus:outline-none ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => togglePasswordVisibility('confirm')}
                  aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {passwordsMatch !== null && (
                <span className="absolute inset-y-0 right-10 flex items-center">
                  {passwordsMatch ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <X className="h-5 w-5 text-red-500" />
                  )}
                </span>
              )}
            </div>
            
            {passwordsMatch === false && (
              <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                As senhas não correspondem
              </p>
            )}
          </div>
          
          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={changingPassword || passwordRequirements.length > 0 || passwordsMatch === false || !passwordData.currentPassword}
            >
              {changingPassword ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </form>
      </div>

      {/* Seção de vinculação com o Google */}
      <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Contas Vinculadas</h3>
        
        {identityError && (
          <div className={`border px-4 py-3 rounded mb-6 ${theme === 'dark' ? 'bg-red-900/20 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
            {identityError}
          </div>
        )}
        
        {loadingIdentities ? (
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Carregando informações...</p>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Google</p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {hasGoogleIdentity ? 'Conta vinculada' : 'Vincular sua conta do Google para login mais fácil'}
                </p>
              </div>
              
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLinkGoogleAccount}
                  disabled={hasGoogleIdentity || isLinkingGoogle}
                >
                  {hasGoogleIdentity ? (
                    'Vinculado'
                  ) : isLinkingGoogle ? (
                    'Vinculando...'
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Vincular Conta Google
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {linkMessage && (
              <div className={`p-3 mt-3 rounded ${
                linkMessage.type === 'success' ? (theme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800') : 
                linkMessage.type === 'error' ? (theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800') : 
                (theme === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800')
              }`}>
                {linkMessage.text}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Seção de Configurações de Notificações */}
      <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Configurações de Notificações</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Notificações Push</p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Receba alertas quando houver interações com seu conteúdo
              </p>
            </div>
            
            <div className="flex items-center">
              <Button
                type="button"
                onClick={async () => {
                  try {
                    // Importar função de forma dinâmica para evitar problemas de inicialização
                    const { requestNotificationPermission } = await import('../lib/firebase/messaging');
                    await requestNotificationPermission();
                    //Idealmente, aqui também salvaríamos o token no perfil do usuário
                    //Mas a função requestNotificationPermission já faz isso se o usuário permitir
                    toast.success("Configuração de notificações atualizada!"); 
                  } catch (err) {
                    console.error('Erro ao configurar notificações:', err);
                    setError('Não foi possível configurar notificações. Tente novamente.');
                  }
                }}
                // Shadcn Button já lida com temas
              >
                Ativar Notificações
              </Button>

              <Button
                type="button"
                variant="outline" // Shadcn Button já lida com temas
                className="ml-3"
                onClick={async () => {
                   try {
                    if (user && user.id) { // Adicionar esta verificação
                      // Remover token FCM do perfil
                      const { error } = await supabase
                        .from('profiles')
                        .update({ fcm_token: null })
                        .eq('id', user.id);

                      if (error) throw error;
                      toast.success("Notificações desativadas!");
                    } else {
                      toast.error("Usuário não encontrado para desativar notificações.");
                    }
                  } catch (err) {
                    console.error('Erro ao desativar notificações:', err);
                    setError('Não foi possível desativar notificações. Tente novamente.');
                  }
                }}
              >
                Desativar Notificações
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Seção de Exclusão de Conta */}
      <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-border' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>Zona de Perigo</h3>
        
        <div className={`border p-4 mb-6 rounded ${theme === 'dark' ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
          <p className={`font-medium ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>Excluir sua conta</p>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>
            Esta ação é irreversível. Todos os seus dados, incluindo perfil, coleções e avaliações serão permanentemente excluídos.
          </p>
        </div>
        
        <Button
          variant="destructive"
          asChild
        >
          <Link to="/excluir-conta">
            Excluir minha conta
          </Link>
        </Button>
      </div>
    </div>
  );
}